// @oasis-permission-inherits: /cloudflare-redirects (redirects:manage)
import { KnownApiError } from "@mirage-web/networking/known-api-error";
import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useState } from "react";
import { toast } from "sonner";

import { Button } from "~/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "~/components/ui/card";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { getApiError } from "~/lib/network/errors";
import {
  getRedirectsStatusMutation,
  previewRedirectsMutation,
  removeRedirectMutation,
} from "~/modules/cloudflare-redirects/mutations";
import { toastPersistentError } from "~/modules/cloudflare-redirects/toasts";

export const Route = createFileRoute("/_dash/cloudflare-redirects/remove/")({
  component: RouteComponent,
});

function RouteComponent() {
  const [sourceUrl, setSourceUrl] = useState("");
  const [isRemoving, setIsRemoving] = useState(false);

  const { mutateAsync: previewChanges } = previewRedirectsMutation.useMutation();
  const { mutateAsync: removeRedirect } = removeRedirectMutation.useMutation();
  const { mutateAsync: pollStatus } = getRedirectsStatusMutation.useMutation();

  const fetchCurrentChecksum = useCallback(async () => {
    const diff = await previewChanges({ params: { csvContent: "" } });
    return diff.checksum;
  }, [previewChanges]);

  const handleRemove = useCallback(async () => {
    const trimmed = sourceUrl.trim();
    if (!trimmed) {
      return;
    }
    setIsRemoving(true);

    let expectedChecksum: string;
    try {
      expectedChecksum = await fetchCurrentChecksum();
    } catch (error) {
      setIsRemoving(false);
      const apiError = error instanceof Error ? getApiError(error) : undefined;
      toastPersistentError(
        apiError?.message ??
          (error instanceof Error ? error.message : undefined) ??
          "Failed to load the current redirect list"
      );
      return;
    }

    let baselineOperationId: string | null = null;
    let baselineKnown = true;
    try {
      const baseline = await pollStatus({ params: {} });
      if (baseline.status !== "none") {
        baselineOperationId = baseline.operationId;
      }
    } catch {
      baselineKnown = false;
    }

    let operationId: string | null = null;
    try {
      const result = await removeRedirect({ params: { sourceUrl: trimmed, expectedChecksum } });
      operationId = result.operationId;
    } catch (error) {
      if (error instanceof KnownApiError) {
        setIsRemoving(false);
        if (error.responseStatus === 404) {
          toastPersistentError(`No redirect found with source URL "${trimmed}".`);
        } else if (error.responseStatus === 409) {
          toastPersistentError(
            "The redirect list changed or another operation is in progress. Please try again."
          );
        } else {
          const apiError = getApiError(error);
          toastPersistentError(apiError?.message ?? error.message ?? "Failed to remove redirect");
        }
        return;
      }
    }

    const isOurOperation = (statusOperationId: string) => {
      if (operationId !== null) {
        return statusOperationId === operationId;
      }
      return baselineKnown && statusOperationId !== baselineOperationId;
    };

    const maxAttempts = 60;
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      await new Promise<void>((resolve) => setTimeout(resolve, 2000));
      try {
        const status = await pollStatus({ params: {} });
        if (status.status === "completed" && isOurOperation(status.operationId)) {
          setIsRemoving(false);
          toast.success(`Redirect "${trimmed}" removed.`);
          setSourceUrl("");
          return;
        }
        if (status.status === "failed" && isOurOperation(status.operationId)) {
          setIsRemoving(false);
          toastPersistentError(status.error);
          return;
        }
      } catch {
        continue;
      }
    }

    setIsRemoving(false);
    toastPersistentError("Operation timed out");
  }, [sourceUrl, fetchCurrentChecksum, removeRedirect, pollStatus]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Remove redirect</CardTitle>
        <CardDescription>
          Remove a single redirect from the Cloudflare list by its source URL.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="source-url">Source URL</Label>
          <Input
            id="source-url"
            className="font-mono"
            placeholder="captions.ai/old-path"
            value={sourceUrl}
            onChange={(e) => setSourceUrl(e.target.value)}
            disabled={isRemoving}
          />
        </div>

        <Button onClick={handleRemove} disabled={!sourceUrl.trim() || isRemoving}>
          {isRemoving ? "Removing..." : "Remove redirect"}
        </Button>
      </CardContent>
    </Card>
  );
}
